'use client'

import { Fragment, PropsWithChildren } from 'react'
import { twJoin, twMerge } from 'tailwind-merge'

import { Listing } from '@/data/getListing'
import { parseAttributes } from '@/utils/parseAttributes'
import { FavouriteIcon } from '../Favourites/FavouriteIcon'

type ListingAttributesProps = PropsWithChildren<{
  listing: Listing
  className?: string
}>

export const ListingAttributes = ({ listing, className }: ListingAttributesProps) => {
  const icons = parseAttributes(listing, true, ['bedrooms', 'bathrooms', 'garages', 'gym', 'study', 'pool', 'tennis'])

  return (
    <div
      className={twMerge(
        'pointer-events-none bottom-0 left-0 top-0 z-40 w-full xl:absolute xl:inset-y-0 xl:left-auto xl:right-7 xl:flex xl:w-auto xl:flex-col',
        className,
      )}
    >
      <div className="pointer-events-auto top-0 flex flex-col gap-y-5 xl:sticky xl:top-1/2 xl:mt-screen/2 xl:flex-row xl:gap-x-4 xl:pb-0 xl:-translate-y-1/2">
        {/* Mobile up to xl */}
        <div className="mt-6 w-full text-balance text-center font-body text-xs xl:hidden">
          {icons.map(([field, _, value, Icon]) => (
            <Fragment key={field}>
              <span className="inline-flex h-6 w-16 flex-row items-center justify-center text-nowrap text-center">
                {/* Need dummy span otherwise icon-less values drop to baseline */}
                {Icon ? <Icon /> : <span className="h-6"></span>}
                <span className="ml-2 inline-flex items-center text-center text-xs tracking-widest">{value}</span>
              </span>{' '}
            </Fragment>
          ))}
          <span className="inline-flex w-16 flex-row justify-center text-nowrap text-center">
            <FavouriteIcon id={listing.id} className="h-6 w-6" />
          </span>
        </div>
        {/* XL and above */}
        <div className="hidden h-auto w-full flex-wrap gap-y-5 text-xs xl:flex xl:w-12 xl:flex-wrap xl:content-start xl:justify-end">
          {icons.map(([field, _, value, Icon]) => (
            <div key={field} className={twJoin('flex items-center gap-x-1', !Icon && 'justify-center')}>
              {Icon && (
                <div className="flex h-6 basis-1/2 justify-end">
                  <Icon />
                </div>
              )}
              <div className={twJoin('h-6 text-sm', Icon && 'flex basis-1/2 justify-start pl-4 leading-6')}>
                {value}
                <span className="sr-only"> {field}</span>
              </div>
            </div>
          ))}
          <div className="flex w-full justify-center">
            <FavouriteIcon id={listing.id} className="h-6 w-6" />
          </div>
        </div>
      </div>
    </div>
  )
}
